import React from 'react';
import PropTypes from 'prop-types';
import { usePrefs } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import DeferredComponent from '../ws-scripts/components/DeferredComponent';
import { useCarouselTracking } from '../hooks/useCarouselTracking';
import { getCardTrackingData } from '../utils';

import Carousel from './Carousel';
import Vehicle from './Vehicle';

const ArrowControl = ({ direction, disabled, onClick }) => {
	const isPrevious = direction === 'previous';
	return (
		<button
			type="button"
			className={setClassNames([
				'rec-vehicles-carousel-arrow',
				`rec-vehicles-carousel-arrow-${direction}`,
				'bg-neutral-0',
				'border-0',
				'rounded-circle',
				'd-flex',
				'align-items-center',
				'justify-content-center',
				'p-0'
			])}
			onClick={onClick}
			disabled={disabled}
			aria-label={isPrevious ? 'Previous vehicles' : 'Next vehicles'}
			data-testid={`carousel-arrow-${direction}`}
		>
			<svg width="12" height="20" viewBox="0 0 12 20" aria-hidden="true">
				<path
					d={isPrevious ? 'M10 2 2 10l8 8' : 'm2 2 8 8-8 8'}
					fill="none"
					stroke="currentColor"
					strokeWidth="2.5"
				/>
			</svg>
		</button>
	);
};

ArrowControl.propTypes = {
	direction: PropTypes.oneOf(['previous', 'next']).isRequired,
	disabled: PropTypes.bool.isRequired,
	onClick: PropTypes.func.isRequired
};

/**
 * Desktop Standard Horizontal carousel, used only when the vehicles do not all
 * fit in one row. The mobile carousel lives in Slider.jsx.
 */
export default function DesktopCarousel({
	vehicleData,
	slidesToShow,
	trackEventElement,
	pageAlias,
	placeholder
}) {
	const { listItemClasses } = usePrefs();
	const lastSlide = Math.max(vehicleData.length - slidesToShow, 0);
	const { afterSlide } = useCarouselTracking({
		trackEventElement,
		pageAlias,
		action: 'clicked'
	});

	const carouselProps = {
		cellAlign: 'left',
		cellSpacing: 20,
		className: 'rec-vehicles-desktop-carousel px-7',
		heightMode: 'max',
		slidesToShow,
		slidesToScroll: 1,
		dragging: false,
		renderBottomCenterControls: null,
		renderCenterLeftControls: ({ previousSlide, currentSlide }) => (
			<ArrowControl
				direction="previous"
				disabled={currentSlide <= 0}
				onClick={previousSlide}
			/>
		),
		renderCenterRightControls: ({ nextSlide, currentSlide }) => (
			<ArrowControl
				direction="next"
				disabled={currentSlide >= lastSlide}
				onClick={nextSlide}
			/>
		),
		enableKeyboardControls: true,
		afterSlide
	};

	const vehicleCardClasses = [
		listItemClasses !== 'BLANK' ? listItemClasses : '',
		'vehicle-card',
		'overflow-hidden',
		'position-relative',
		'my-1',
		'mb-4',
		'bg-contrast-low'
	];

	return (
		<DeferredComponent placeholder={placeholder}>
			<Carousel {...carouselProps}>
				{vehicleData.map((vehicle, index) => (
					<div
						className={setClassNames(vehicleCardClasses)}
						key={vehicle.uuid}
						data-testid="card-carousel-slide"
					>
						<Vehicle
							info={vehicle}
							index={index}
							trackEventElement={trackEventElement}
							trackingData={getCardTrackingData(vehicle)}
							pageAlias={pageAlias}
						/>
					</div>
				))}
			</Carousel>
		</DeferredComponent>
	);
}

DesktopCarousel.propTypes = {
	vehicleData: PropTypes.arrayOf(
		PropTypes.shape({
			uuid: PropTypes.string.isRequired
		})
	).isRequired,
	slidesToShow: PropTypes.number.isRequired,
	trackEventElement: PropTypes.string.isRequired,
	pageAlias: PropTypes.string.isRequired,
	placeholder: PropTypes.element
};
